import uniqid from 'uniqid';
import {
  extractSelectedTicketFromState,
  extractAllMacroWizardPaths,
  extractCurrentDecisionTreePathArray,
  extractRootDecisionTreeAtPath,
  extractSimplrTagForSelectedTicket,
  getSearchId,
  getSimplrAccountIdOfSelectedTicket,
  getBrandOfSelectedTicket,
  extractSimplrTagsForSelectedTicket,
  extractSelectedInboxTab,
  extractNodeFromSpecifiedTreeLocation,
  getSimplrTaggingActive,
  extractNonHistoricalTicketIdFromState,
  extractInProgressTreeAndWizardLocation,
  getSelectedTicketBrand,
  extractTicketFromStateById,
  getShowToolsState,
  extractCurrentDecisionTreePath,
} from './../extractFromState';
import { setLoading, setLoadingForMacroWizard } from './index';
import { showErrorMessage, showError } from '../util/errorPopupUtil';
import { removeLastTagFromPath } from '../util/formatUtil';
import { getTreeAtPath } from '../util/decisionTreeUtil';
import { clearBlockingAlertViewedIds } from './tools/ToolActions';
import {
  hydrateInProgressTreeAndWizardLocation,
  hydrateInProgressTreeAndWizardForPartnerReachOut,
} from 'actions/inProgressTreeAndWizardLocationActions';
import { setTagForTicket } from './tools/TaggingActions';
import { getZendeskComments, searchTicketsInZendeskByKeyword } from 'api/tickets/zendesk';
import { deleteAlgoliaObject, addAlgoliaObject } from '../api/algolia';
import { getBusinessAlertsForAccount, getBusinessAlertReadData } from '../api/businessAlerts';
import { getMacrosForAccount } from '../api/macroArticles';
import * as types from './types';
import * as searchTypes from '../constants/searchTypes';
import { DECISION_TREE_SPINNER_ACTIONS } from '../constants/decisionTree';
import * as orderSearchTypes from '../constants/ordersSearchActionTypes';
import * as subTypes from '../constants/subscriptionSearch';
import {
  trackSearchQuerySubmitted,
  trackMacroWizardJumpToTagPath,
  trackDecisionTreeBreadcrumbClick,
  trackDecisionTreePathChanged,
} from '../analytics/searchEventAnalytics';
import {
  getUniqueIdOfSearchResultAndType,
  organizeMacroData,
  checkIfBusinessAlertIsRead,
  mapZendeskTicektsToAlgoliaFormat,
} from '../util';
import { isValidPartnerReachOutTicket } from '../util/ticketSelector';
import { addEmptyArraysAsValuesToObject } from '../util/formatUtil';
import { processBusinessAlerts } from '../util/dataObjectToStoreObject';
import keyBy from 'lodash/keyBy';
import { ZENDESK } from '../constants/conversationTypes';
import isEmpty from 'lodash/isEmpty';
import { ErrorStrings } from '../constants/fixedMessages';
import { clearExtraLastMileInfo } from '../actions/tools/OrdersSearchActions';
import { fetchDecisionTrees, fetchDecisionTreesAndLogicFlows } from 'api/decisionTree';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import { handleError } from 'services/ErrorService/ErrorService';
import makeCloudFunctionRequest from 'api/makeCloudFunctionRequest';

const resultTypeToIndexName = {
  [searchTypes.SEARCH_TICKETS]: 'TICKETS',
  [searchTypes.SEARCH_KB]: 'KB_AND_MACROS',
  [searchTypes.SEARCH_MACROS]: 'KB_AND_MACROS',
};

// normal actions
export const setDeletedSearchResult = (result, resultType) => {
  // to prevent any clashes of ids prepend with id with the resultType
  const uniqueId = getUniqueIdOfSearchResultAndType(result, resultType);

  return { type: types.SET_DELETED_SEARCH_RESULT, uniqueId, result };
};

export const removeDeletedSearchResult = (uniqueId) => ({ type: types.CLEAR_DELETED_SEARCH_RESULT, uniqueId });

export const selectSearchResultTicket = (resultType, resultIndex) => {
  return {
    type: types.SELECT_TICKET_SEARCH_RESULT,
    resultType,
    resultIndex,
  };
};

export const setResultIndex = (resultIndex) => {
  return {
    type: types.SET_RESULT_INDEX,
    resultIndex,
  };
};

// async actions
export const deleteSearchResultOfType = (result, resultType) => async (dispatch, getState) => {
  try {
    const { objectID } = result;
    const { simplrAccountPtrId } = extractSelectedTicketFromState(getState());
    const indexKey = resultTypeToIndexName[resultType];

    trackSearchResultChange('Delete', resultType, objectID);

    const response = await deleteAlgoliaObject(objectID, simplrAccountPtrId, indexKey);

    dispatch(setDeletedSearchResult(response, resultType));

    return response;
  } catch (err) {
    showError(err);

    return null;
  }
};

export const restoreSearchResultOfType = (result, resultType) => async (dispatch, getState) => {
  try {
    const { simplrAccountPtrId } = extractSelectedTicketFromState(getState());
    const indexKey = resultTypeToIndexName[resultType];
    const uniqueId = getUniqueIdOfSearchResultAndType(result, resultType);

    trackSearchResultChange('Restore', resultType, result.objectID);

    const deletedResult = getState().tools.ticketSearch.deletedResultsById[uniqueId];
    const response = await addAlgoliaObject(deletedResult, simplrAccountPtrId, indexKey);

    dispatch(removeDeletedSearchResult(uniqueId));

    return response;
  } catch (err) {
    showError(err);

    return null;
  }
};

const trackSearchResultChange = (action, resultType, objectID) => {
  SessionAnalyticsService.event(`Search - Specialist Clicked ${action}`, {
    objectID,
    resultType,
  });
};

const createSearchId = () => uniqid();

export const setSearchId = (searchId) => ({
  type: types.SET_SEARCH_ID,
  searchId,
});

export const searchAll = (searchQuery, isUserSearch) => {
  return (dispatch, getState) => {
    const { ALL_SEARCH_RESULTS, ALL_SEARCH_ERROR } = types;

    dispatch({
      type: ALL_SEARCH_RESULTS,
      macroResults: null,
      kbResults: null,
      ticketResults: null,
      query: searchQuery,
    });

    const state = getState();

    const selectedTicket = extractSelectedTicketFromState(state) || {};
    if (!selectedTicket) {
      return showErrorMessage(ErrorStrings.SELECTED_TICKET_NOT_FOUND, true);
    }

    const brand = getBrandOfSelectedTicket(state);
    const simplrAccountId = selectedTicket.simplrAccountPtrId;
    const conversationId = selectedTicket.ticketId;

    const numMacroResults = 5;
    const numKbResults = 2;
    const numTicketResults = 50;

    const macroSearchObject = {
      query: searchQuery,
      attributesToHighlight: ['fullSubheading', 'text'],
      attributesToSnippet: ['fullSubheading', 'text:30'],
      snippetEllipsisText: '...',
      page: 0,
      hitsPerPage: numMacroResults,
    };
    const kbSearchObject = {
      query: searchQuery,
      attributesToHighlight: ['fullSubheading', 'text'],
      attributesToSnippet: ['fullSubheading', 'text:30'],
      snippetEllipsisText: '...',
      page: 0,
      hitsPerPage: numKbResults,
    };
    const ticketSearchObject = {
      query: searchQuery,
      attributesToHighlight: ['messages', 'subjects'],
      attributesToSnippet: ['messages:30', 'subjects'],
      snippetEllipsisText: '...',
      page: 0,
      hitsPerPage: numTicketResults,
    };

    if (brand) {
      macroSearchObject.filters = `brand: "${brand}" OR brand: "undefined"`;
      kbSearchObject.filters = `brand: "${brand}" OR brand: "undefined"`;
      ticketSearchObject.filters = `brand: "${brand}" OR brand: "undefined"`;
    }

    const params = {
      simplrAccountId,
      macroSearchObject,
      kbSearchObject,
      ticketSearchObject,
      conversationId,
    };

    dispatch(setSearchId(createSearchId())); // for search analytics tracking
    const searchId = getSearchId(getState());
    // call and forget - we don't want this to be blocking
    trackSearchQuerySubmitted(searchId, searchQuery, conversationId, simplrAccountId);

    return makeCloudFunctionRequest('searchMacrosKbArticlesAndConversations', params)
      .then((results) => {
        if (
          results &&
          ((results.MACROS && results.MACROS.hits.length) ||
            (results.KB_ARTICLES && results.KB_ARTICLES.hits.length) ||
            (results.TICKETS && results.TICKETS.hits.length))
        ) {
          dispatch(setLoading(false, 'searchPage'));
          // we don't want to save the automatic query in the redux state - it will override the placeholder text
          dispatch({
            type: ALL_SEARCH_RESULTS,
            macroResults: results.MACROS,
            kbResults: results.KB_ARTICLES,
            ticketResults: results.TICKETS,
            query: isUserSearch ? searchQuery : '',
          });
        } else {
          dispatch(setLoading(false, 'searchPage'));
          dispatch({
            type: ALL_SEARCH_ERROR,
            error: 'No Results Found',
          });
        }
      })
      .catch((err) => {
        dispatch(setLoading(false, 'searchPage'));
        handleError({ error: err, customMessage: 'Error searching macros, KB articles, and tickets' });
        showError(err);
      });
  };
};

export const setBusinessAlerts = (businessAlerts) => {
  const accountIdsToBusinessAlerts = keyBy(businessAlerts, (b) => b.id);

  return {
    type: 'SET_BUSINESS_ALERTS',
    businessAlerts: accountIdsToBusinessAlerts,
  };
};

export const clearBusinessAlerts = () => ({
  type: 'CLEAR_BUSINESS_ALERTS',
});

export const clearSelectedSearchResult = () => ({ type: types.CLEAR_SELECTED_TICKET_RESULT });

export const loadMacrosAndDecisionTreeForAccount = () => async (dispatch, getState) => {
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(getState());
  const isTaggingActive = getSimplrTaggingActive(getState());
  // if no selected ticket, can skip call
  if (!simplrAccountId) {
    return;
  }
  try {
    // load the decision tree as well
    const [macroArticles] = await Promise.all([
      getMacrosForAccount(simplrAccountId),
      dispatch(loadDecisionTreeForBusinessOfSelectedTicket()),
      dispatch(loadLogicFlowsAndDecisionTreesOfSelectedTicket()),
    ]);
    const { categories } = extractSimplrTagsForSelectedTicket(getState());
    const allTagsWithEmptyArrays = addEmptyArraysAsValuesToObject(categories);
    const macroListByPath = organizeMacroData(categories, macroArticles);
    const allPathsWithMacros = Object.assign({}, allTagsWithEmptyArrays, macroListByPath);
    // setMacroListByPath must fire before setPathBasedOffTags
    dispatch(setMacroListByPath(allPathsWithMacros));
    // do not set path by previous tags during tagging
    if (isTaggingActive) {
      dispatch(setDecisionTreePath(''));
      dispatch(setMacroWizardPath(''));
    } else {
      await dispatch(loadPreviousWizardLocation());
    }
  } catch (error) {
    handleError({ error, customMessage: 'Error loading macros', showPopup: true });
  }
};

export const loadBusinessAlertsForAccount = (simplrAccountId) => async (dispatch, getState) => {
  if (!simplrAccountId) {
    simplrAccountId = getSimplrAccountIdOfSelectedTicket(getState());
  }
  // if no selected ticket, can skip call
  if (!simplrAccountId) {
    return;
  }

  try {
    const [businessAlertData, businessAlertReadData] = await Promise.all([
      getBusinessAlertsForAccount(simplrAccountId),
      getBusinessAlertReadData(simplrAccountId),
    ]);
    const businessAlerts = businessAlertData?.map(processBusinessAlerts);
    const businessAlertsWithReadData = checkIfBusinessAlertIsRead(businessAlerts, businessAlertReadData);
    dispatch(setBusinessAlerts(businessAlertsWithReadData));
  } catch (error) {
    handleError({ error, customMessage: 'Error loading partner alerts', showPopup: true });
  }
};

export const loadPreviousWizardLocation = () => async (dispatch, getState) => {
  const state = getState();
  const selectedTicketId = extractNonHistoricalTicketIdFromState(state);
  const storedLocationFromCurrentSession = extractInProgressTreeAndWizardLocation(state, selectedTicketId);
  const ticket = extractTicketFromStateById(state, selectedTicketId);

  if (isValidPartnerReachOutTicket(ticket)) {
    await dispatch(hydrateInProgressTreeAndWizardForPartnerReachOut(selectedTicketId));
  } else if (storedLocationFromCurrentSession) {
    await dispatch(hydrateInProgressTreeAndWizardLocation(selectedTicketId));
  } else {
    dispatch(setPathBasedOffTags());
  }
};

export const setPathBasedOffTags = () => (dispatch, getState) => {
  const state = getState();
  const tag = extractSimplrTagForSelectedTicket(state);
  // validate the path leads to a macro
  const allPaths = extractAllMacroWizardPaths(state);
  const tagExistsInAvailablePaths = allPaths.includes(tag);
  if (tagExistsInAvailablePaths) {
    trackMacroWizardJumpToTagPath(tag);

    return dispatch(setMacroWizardPath(tag, true));
  }
  if (tag && getShowToolsState(state)) {
    handleError({
      customMessage: 'Tags have been updated for this partner and this ticket still has an old tag. Please tag again',
      showPopup: true,
      popupOnly: true,
    });
  }
  dispatch(setDecisionTreePath(''));
  dispatch(setMacroWizardPath(''));
};

export const loadDecisionTreeForBusinessOfSelectedTicket = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
    const brand = getSelectedTicketBrand(state);
    const decisionTrees = await fetchDecisionTrees({ simplrAccountId, brand });
    // selected ticket may have changed during network request
    const ticketIsStillSelected = getSimplrAccountIdOfSelectedTicket(getState()) === simplrAccountId;
    if (ticketIsStillSelected) {
      dispatch(setDecisionTrees(decisionTrees, simplrAccountId));
    }
  } catch (e) {
    showError(e);
  }
};

export const setDecisionTrees = (trees, simplrAccountId) => ({
  type: types.SET_DECISION_TREES,
  trees,
  simplrAccountId,
});

export const loadLogicFlowsAndDecisionTreesOfSelectedTicket = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
    const brand = getSelectedTicketBrand(state);
    const logicFlowsAndDecisionTrees = await fetchDecisionTreesAndLogicFlows({ simplrAccountId, brand });
    // selected ticket may have changed during network request
    const ticketIsStillSelected = getSimplrAccountIdOfSelectedTicket(getState()) === simplrAccountId;
    if (ticketIsStillSelected) {
      dispatch(setDecisionFlowsData(logicFlowsAndDecisionTrees, simplrAccountId));
    }
  } catch (e) {
    showError(e);
  }
};

export const setDecisionFlowsData = (decisionFlowsData, simplrAccountId) => ({
  type: types.SET_DECISION_FLOWS_DATA,
  decisionFlowsData,
  simplrAccountId,
});

// Set decision tree path based off the depth of clicking on a breadcrumb
// note the depth will depend on nodes that have breadcrumb=false
export const setDecisionTreeDepth = (depth) => (dispatch, getState) => {
  let pathArray = extractCurrentDecisionTreePathArray(getState());
  let currTree = extractRootDecisionTreeAtPath(getState());

  // build the new path
  const newPath = [];

  // start j at one
  let j = 0;
  for (let i = 0; i < pathArray.length - 1; i++) {
    // get the full node based off the id
    const decisionNode = extractNodeFromSpecifiedTreeLocation(currTree);

    // increase our j value if the node has a breadcrumb and we are passed position 0
    if (decisionNode.breadcrumb && i > 0) {
      j++;
      // quit if at depth
      if (j >= depth) {
        break;
      }
    }

    const choice = pathArray[i];
    newPath.push(choice);

    currTree = currTree.children[choice];
  }

  const decisionTreePath = newPath.join(' -- ');
  trackDecisionTreeBreadcrumbClick();

  dispatch(setDecisionTreePath(decisionTreePath));
};

export const setDecisionTreePath = (decisionTreePath) => ({
  type: types.SET_DECISION_TREE_PATH,
  decisionTreePath,
});

export const setMacroListByPath = (macroListByPath) => ({
  type: types.SET_MACROS_BY_TAG_PATH,
  macroListByPath,
});

export const setCategoryInstructions = (data) => ({
  type: 'SET_CATEGORY_INSTRUCTIONS',
  data,
});

export const setPathWasReset = (value) => ({
  type: 'SET_PATH_WAS_RESET',
  value,
});

export const setMacroWizardPath = (tagPath, skipTagging) => async (dispatch, getState) => {
  const currentTab = extractSelectedInboxTab(getState());
  const isRetaggingAllowed = currentTab === 'assigned' || currentTab === 'live_chat';
  const isTaggingActive = getSimplrTaggingActive(getState());
  const isTaggingComplete = tagPath.includes('--');

  dispatch(clearBlockingAlertViewedIds());
  if ((isTaggingActive || isRetaggingAllowed) && isTaggingComplete && !skipTagging) {
    dispatch(setLoadingForMacroWizard(true));
    await dispatch(setTagForTicket(tagPath));
    dispatch(setLoadingForMacroWizard(false));
  }

  dispatch({
    type: 'SET_MACRO_WIZARD_PATH',
    tagPath,
  });

  dispatch(clearExtraLastMileInfo());
};

export const clearMacroWizardPath = () => ({
  type: 'CLEAR_MACRO_WIZARD_PATH',
});

export const setSelectedWizardMacro = (macro) => ({
  type: 'SET_SELECTED_WIZARD_MACRO',
  macro,
});

export const clearSelectedWizardMacro = () => ({
  type: 'CLEAR_SELECTED_WIZARD_MACRO',
});

export const setNoMatchPage = (value) => ({
  type: 'SET_NO_MATCH_PAGE',
  value,
});

export const returnToPreviousMacroWizardPath = () => async (dispatch, getState) => {
  const state = getState();
  const oldPath = extractCurrentDecisionTreePath(state);
  let currTree = extractRootDecisionTreeAtPath(state);

  // During reverse tree navigation, do not allow user to stop
  // on PROCESSING node which will push them forward again
  const recursivelyBackUp = (currentDecisionTreePath) => {
    if (currentDecisionTreePath) {
      const newPath = removeLastTagFromPath(currentDecisionTreePath);
      const currentTreeLocation = getTreeAtPath(currTree, newPath);

      if (DECISION_TREE_SPINNER_ACTIONS.includes(currentTreeLocation?.node?.action)) {
        return recursivelyBackUp(newPath);
      }

      dispatch(setDecisionTreePath(newPath));
    } else {
      dispatch(setDecisionTreePath(currentDecisionTreePath));
    }
  };

  if (oldPath) {
    return recursivelyBackUp(oldPath);
  }

  dispatch({
    type: 'RETURN_TO_PREVIOUS_MACRO_PATH',
  });
};

export const addEdgeToDecisionTreePath = (choice) => {
  trackDecisionTreePathChanged(choice);

  return {
    type: 'ADD_EDGE_TO_DECISION_TREE_PATH',
    choice,
  };
};

export const setSubscriptionOrders = (orders) => ({
  type: subTypes.SET_SUBSCRIPTION_ORDERS,
  orders,
});

export const setInvalidSnippetData = (invalidSnippetData) => ({
  type: orderSearchTypes.SET_INVALID_SNIPPET_DATA,
  invalidSnippetData,
});

export const addSnippetText = (text) => ({
  type: orderSearchTypes.ADD_SNIPPET_TEXT,
  text,
});

export const setSnippetText = (snippetTextList) => ({
  type: orderSearchTypes.SET_SNIPPET_TEXT,
  snippetTextList,
});

export const setShippoTrackingInfo = (shipmentId, shippoTracking) => ({
  type: orderSearchTypes.SET_SHIPPO_TRACKING_INFO,
  shipmentId,
  shippoTracking,
});

export const toggleAlertShowHide = (alertId) => ({
  type: 'TOGGLE_ALERT_SHOW_HIDE',
  alertId,
});

export const setDiscountPeriods = (discountPeriods) => ({
  type: orderSearchTypes.SET_DISCOUNT_PERIODS,
  discountPeriods,
});

export const setItemAndDiscountPeriodMatches = (itemAndDiscountPeriodMatches) => ({
  type: orderSearchTypes.SET_ITEM_AND_DISCOUNT_PERIOD_MATCHES,
  itemAndDiscountPeriodMatches,
});

export const setSelectedDiscountPeriod = (selectedDiscountPeriod) => ({
  type: orderSearchTypes.SET_SELECTED_DISCOUNT_PERIOD,
  selectedDiscountPeriod,
});

export const setSelectedCouponCodeType = (couponType) => ({
  type: orderSearchTypes.SET_SELECTED_COUPON_CODE_TYPE,
  couponType,
});

export const searchInPartersTicketingSystem =
  (searchValue, selectedTicket, setLoadingLocal) => async (dispatch, getState) => {
    const { ticketType } = selectedTicket;

    switch (ticketType) {
      case ZENDESK:
        try {
          // cleanup previous result first
          dispatch({
            type: types.SET_PARTNER_PLATFORM_HISTORICAL_TICKETS,
            payload: {},
          });

          const { platformDomain, simplrAccountPtrId } = selectedTicket;
          const result = (await searchTicketsInZendeskByKeyword(platformDomain, simplrAccountPtrId, searchValue)) || [];

          dispatch({
            type: types.SET_PARTNER_PLATFORM_HISTORICAL_TICKETS,
            payload: mapZendeskTicektsToAlgoliaFormat(result),
          });
        } catch (error) {
          handleError({ error });
        } finally {
          setLoadingLocal && setLoadingLocal(false);
        }
        break;
      default:
        return null;
    }
  };

export const selectParnerPlatformTicketForView =
  (selectedParnerPlatformTicket, selectedTicket, index) => async (dispatch) => {
    const { ticketType } = selectedTicket;

    switch (ticketType) {
      case ZENDESK:
        // zendesk tickets in the results list have only 1 message in the messages array
        // this logic will call zd api to get all ticket's messages and update the result value in state
        try {
          const { platformTicketId } = selectedParnerPlatformTicket;
          const { platformDomain, simplrAccountPtrId } = selectedTicket;
          const ticketComments = await getZendeskComments(platformDomain, platformTicketId, simplrAccountPtrId);

          let customerId;

          if (!isEmpty(ticketComments)) {
            const { comments } = ticketComments;
            const [ticketSubject] = selectedParnerPlatformTicket.subjects;
            const updatedMessagesAndDirections = comments.reduce(
              (result, commentObject, idx) => {
                // there's no messages direction data in the zd api response
                // so we always use the very first message's author_id as customer id
                // messages with other author_id considered OUTBOUND (sent by an expert)
                const messageAuthorId = commentObject.author_id;
                if (idx === 0) {
                  customerId = messageAuthorId;
                }
                const source = messageAuthorId === customerId ? 'INBOUND' : 'OUTBOUND';
                const message = commentObject.html_body;

                result.directions = [...result.directions, source];
                result.messages = [...result.messages, message];

                // altering tickets subject based on message direction
                const subject = source === 'OUTBOUND' ? `Re: ${ticketSubject}` : ticketSubject;

                result._snippetResult = {
                  subjects: [...result._snippetResult.subjects, { value: subject, matchLevel: 'full' }],
                  messages: [...result._snippetResult.messages, { value: message, matchLevel: 'full' }],
                };
                result._highlightResult = {
                  subjects: [
                    ...result._highlightResult.subjects,
                    {
                      value: subject,
                      matchLevel: 'full',
                    },
                  ],
                  messages: [
                    ...result._highlightResult.messages,
                    {
                      value: message,
                      matchLevel: 'full',
                    },
                  ],
                };

                return result;
              },
              {
                messages: [],
                directions: [],
                _snippetResult: {
                  subjects: [],
                  messages: [],
                },
                _highlightResult: {
                  subjects: [],
                  messages: [],
                },
              }
            );

            const updatedTicket = Object.assign({}, selectedParnerPlatformTicket, updatedMessagesAndDirections);

            dispatch({
              type: types.UPDATE_PARTNER_PLATFORM_HISTORICAL_TICKET,
              updatedTicket,
              index,
            });

            dispatch({
              type: types.SELECT_TICKET_SEARCH_RESULT,
              resultType: searchTypes.SEARCH_PARTNER_PLATFORM_HISTORICAL_TICKETS,
              resultIndex: index,
            });
          }
        } catch (error) {
          handleError({
            error,
            customMessage: 'Something went wrong. Please try again later',
            showPopup: true,
          });
        }
        break;
      default:
        return null;
    }
  };

export const setOrderSearch = (data) => ({
  type: orderSearchTypes.SET_ORDER_SEARCH,
  data,
});

export const setSubscriptionSearch = (data) => ({
  type: subTypes.SET_SUBSCRIPTION_SEARCH,
  data,
});

export const restartDecisionFlowRequested = (restartDecisionFlowData) => ({
  type: types.RESTART_DECISION_FLOW_REQUESTED,
  restartDecisionFlowData,
});
